// Paginated scanner results table. 10 rows per page, newest first.
// Pure display — parent owns filter state and passes it as props.
// Data fetched from GET /api/scan-history with server-side filters.

import { useMemo, useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Trash2, X } from 'lucide-react';
import Chip from '../ui/Chip';
import { apiDelete, apiGet } from '../../lib/api';
import type { ScanHistoryResponse, ScanHistoryRow } from '../../lib/types';

const PER_PAGE = 10;

export interface ScanHistoryFilters {
  sources: string[];            // e.g. ["api","linkedin"] — empty = all
  postedWithinHours: number | null;
  q: string;
  withSalary: boolean;
  country?: string;             // e.g. "UK" — applies to all sources including API portals
  // Free-form scan search term ("AI/ML Lecturer; Postdoc"). Server
  // tokenizes it and drops rows whose title matches NONE of the
  // tokens. Empty/undefined → no post-filter applied.
  keywords?: string;
  // Audit toggle. When true, rows the title filter / deterministic
  // gate already judged irrelevant (filter-dropped-urls.txt) are
  // included in the table. Default false → Scanner total matches
  // Matches Pending total. Toggle in Scanner.tsx flips this.
  includeDropped?: boolean;
}

// Expand logical source keys to the underlying site values stored in TSV.
function expandSources(keys: string[]): string | undefined {
  if (!keys.length) return undefined;
  const expanded = new Set<string>();
  for (const k of keys) {
    if (k === 'api') {
      ['greenhouse-api', 'ashby-api', 'lever-api', 'workable-api'].forEach((s) => expanded.add(s));
    } else {
      expanded.add(k);
    }
  }
  return Array.from(expanded).join(',');
}

function siteBadgeColor(site: string): string {
  if (/greenhouse/.test(site)) return 'var(--line2)';
  if (/ashby/.test(site)) return '#ece4f5';
  if (/lever/.test(site)) return '#f4e7db';
  if (/workable/.test(site)) return '#e8f0e8';
  if (site === 'linkedin') return '#dbe7f4';
  if (site === 'indeed') return '#dcefd8';
  if (site === 'glassdoor') return '#f1e1cf';
  if (site === 'zip_recruiter') return '#f4d8d8';
  return 'var(--line2)';
}

function siteDisplay(site: string): string {
  if (/-api$/.test(site)) return site.replace('-api', '');
  if (site === 'zip_recruiter') return 'ZipRecruiter';
  return site;
}

function fmtRelative(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 14) return `${d}d ago`;
  return iso.slice(0, 10);
}

function fmtSalary(row: ScanHistoryRow): string {
  if (row.min_amount == null && row.max_amount == null) return '—';
  const cur = row.interval === 'hourly' ? '/hr' : '/yr';
  const fmt = (n: number) => {
    if (n >= 1000) return `$${Math.round(n / 1000)}k`;
    return `$${n}`;
  };
  if (row.min_amount != null && row.max_amount != null) {
    return `${fmt(row.min_amount)}–${fmt(row.max_amount)}${cur}`;
  }
  return `${fmt((row.min_amount ?? row.max_amount) as number)}${cur}`;
}

function fmtLocation(row: ScanHistoryRow): string {
  const parts = [row.city, row.state].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

// Indeed serves a Cloudflare challenge on first hit. Real browsers can usually
// solve it by clicking the "verify you're human" checkbox; after that, the
// Cloudflare cookie lets subsequent Indeed URLs load normally. If the user is
// still blocked (flagged IP etc), the [Copy] button lets them paste the URL
// into another browser or a private window.

interface Props {
  filters: ScanHistoryFilters;
  refreshKey?: number; // bump to force refetch (e.g. after scan completes)
}

export default function ScanHistoryTable({ filters, refreshKey = 0 }: Props) {
  const [page, setPage] = useState(0);
  const qc = useQueryClient();

  // Two-click armed delete: tracks which row's URL is currently armed.
  // Auto-reverts after 3 s if the user doesn't confirm — same pattern
  // as MatchRow (web/client/src/components/domain/MatchRow.tsx). Only
  // one row can be armed at a time so accidental double-arming on
  // adjacent rows isn't possible.
  const [armedUrl, setArmedUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!armedUrl) return;
    const t = setTimeout(() => setArmedUrl(null), 3000);
    return () => clearTimeout(t);
  }, [armedUrl]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [filters]);

  // Per-row delete handler. Mirrors the by-url endpoint called by the
  // Pending tab × button. On success, invalidate the scan-history
  // query so the row vanishes immediately, AND any matches-pending
  // queries (the URL was potentially also visible there).
  const handleDelete = async (e: React.MouseEvent, url: string) => {
    e.stopPropagation();   // prevent the row's open-in-new-tab click
    if (armedUrl !== url) {
      setArmedUrl(url);
      return;
    }
    setArmedUrl(null);
    try {
      await apiDelete(`/api/scan-history/by-url?url=${encodeURIComponent(url)}`);
      qc.invalidateQueries({ queryKey: ['scan-history'] });
      qc.invalidateQueries({ queryKey: ['matches-pending'] });
      qc.invalidateQueries({ queryKey: ['matches-pending-count'] });
    } catch (err) {
      alert('Delete failed: ' + ((err as Error).message || 'unknown'));
    }
  };

  const queryParams = useMemo(
    () => ({
      limit: PER_PAGE,
      offset: page * PER_PAGE,
      sources: expandSources(filters.sources || []),
      posted_within_hours: filters.postedWithinHours ?? undefined,
      q: (filters.q || '').trim() || undefined,
      with_salary: filters.withSalary ? '1' : undefined,
      country: filters.country || undefined,
      keywords: (filters.keywords || '').trim() || undefined,
      include_dropped: filters.includeDropped ? '1' : undefined,
    }),
    [filters, page]
  );

  const result = useQuery({
    queryKey: ['scan-history', queryParams, refreshKey],
    queryFn: () => apiGet<ScanHistoryResponse>('/api/scan-history', queryParams as Record<string, string | number | boolean | undefined>),
    staleTime: 15_000,
  });

  const items = result.data?.items ?? [];
  const total = result.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="card">
      <div
        className="row-head"
        style={{ gridTemplateColumns: '1fr 1.1fr 1.3fr 2fr 1.3fr 0.8fr 1fr 0.9fr' }}
      >
        {['Scanned', 'Source', 'Company', 'Role', 'Location', 'Type', 'Salary', ''].map((h) => (
          <div key={h} className="tab">{h}</div>
        ))}
      </div>

      {result.isLoading && <div className="empty">Loading scan history…</div>}
      {result.isError && (
        <div className="empty">
          <span className="serif">Couldn't load scan history.</span>
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink2)' }}>
            {(result.error as Error)?.message || 'Unknown error'}
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--ink3)' }}>
            Check browser DevTools → Network tab for details. Try a hard-refresh (Cmd+Shift+R).
          </div>
        </div>
      )}
      {!result.isLoading && !result.isError && items.length === 0 && (
        <div className="empty">
          <span className="serif">No results.</span>
          Try relaxing filters or running a scan.
        </div>
      )}

      {items.map((row, i) => (
        <div
          key={`${row.url}-${i}`}
          className="row"
          style={{ gridTemplateColumns: '1fr 1.1fr 1.3fr 2fr 1.3fr 0.8fr 1fr 0.9fr' }}
          onClick={() => window.open(row.url, '_blank', 'noreferrer')}
          title={
            row.site === 'indeed'
              ? 'Indeed may require Cloudflare verification. Click the copy icon if it won\'t open.'
              : row.first_seen
          }
        >
          <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{fmtRelative(row.first_seen)}</div>
          <div>
            <span
              className="chip"
              style={{ background: siteBadgeColor(row.site), fontSize: 10.5, padding: '2px 7px' }}
              title={row.site}
            >
              {siteDisplay(row.site)}
            </span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>{row.company || '—'}</div>
          <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500 }}>{row.title}</div>
          <div style={{ fontSize: 12, color: 'var(--ink2)' }}>{fmtLocation(row)}</div>
          <div style={{ fontSize: 11 }}>
            {row.job_type ? <Chip>{row.job_type}</Chip> : <span style={{ color: 'var(--ink3)' }}>—</span>}
          </div>
          <div style={{ fontSize: 12, color: row.min_amount != null ? 'var(--ink)' : 'var(--ink3)', fontWeight: row.min_amount != null ? 500 : 400 }}>
            {fmtSalary(row)}
          </div>
          <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: 6, alignItems: 'center' }}>
            <ExternalLink size={14} color="var(--ink3)" />
            <button
              className="btn ghost"
              onClick={(e) => handleDelete(e, row.url)}
              style={{
                fontSize: 11,
                padding: '3px 7px',
                lineHeight: 0,
                color: armedUrl === row.url ? '#fff' : '#b43a3a',
                background: armedUrl === row.url ? '#b43a3a' : 'transparent',
                border: armedUrl === row.url ? '1px solid #b43a3a' : '1px solid transparent',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                transition: 'all 120ms',
              }}
              title={armedUrl === row.url ? 'Click again to remove this URL from scan-history' : 'Delete this row from scan-history'}
              aria-label={armedUrl === row.url ? 'Confirm delete' : 'Delete'}
            >
              {armedUrl === row.url ? (
                <>
                  <X size={11} /> Sure?
                </>
              ) : (
                <Trash2 size={12} />
              )}
            </button>
          </div>
        </div>
      ))}

      {total > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid var(--line2)', fontSize: 12 }}>
          <span style={{ color: 'var(--ink3)' }}>
            Page <strong style={{ color: 'var(--ink)' }}>{page + 1}</strong> of <strong style={{ color: 'var(--ink)' }}>{pages}</strong> · {total.toLocaleString()} total
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn" disabled={page === 0} onClick={() => setPage(page - 1)}>← Prev</button>
            <button className="btn" disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}
